import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui/BackButton';
import { ROUTES, SHOPPER_HOME } from '../../constants/routes';

const EFFECTIVE_DATE = 'August 7, 2026';
const LAST_UPDATED = 'August 7, 2026';

interface SectionProps {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, number, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-serif text-heading mb-3">
        {number}. {title}
      </h2>
      <div className="space-y-3 text-sm text-body leading-relaxed">{children}</div>
    </section>
  );
}

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <BackButton fallback={SHOPPER_HOME} />
            <h1 className="ml-4 text-xl font-serif text-heading">Terms of Service</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 space-y-8">
          <div className="border-b border-gray-100 pb-6">
            <p className="text-4xl font-serif text-primary mb-2">Fash</p>
            <h2 className="text-2xl font-serif text-heading">Terms of Service</h2>
            <p className="text-sm text-body mt-3">
              Effective Date: {EFFECTIVE_DATE}
              <span className="mx-2 text-gray-300">·</span>
              Last Updated: {LAST_UPDATED}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 mt-4">
              This is placeholder legal copy pending final review by counsel. Do not rely on
              it for compliance purposes until it has been formally approved.
            </p>
            <p className="text-sm text-body mt-4 leading-relaxed">
              These Terms of Service (&quot;Terms&quot;) govern your access to and use of the Fash
              platform, website, and mobile experiences (collectively, the &quot;Service&quot;). By
              creating an account or using the Service, you agree to these Terms.
            </p>
          </div>

          <Section id="acceptance" number="1" title="Acceptance">
            <p>
              By accessing or using Fash, you confirm that you are at least 18 years old and
              agree to be bound by these Terms and our{' '}
              <Link to={ROUTES.privacy} className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>

          <Section id="service" number="2" title="The Service">
            <p>
              Fash is a marketplace that connects shoppers with independent boutiques for
              fashion purchases and delivery. Fash is not the seller of merchandise listed by
              boutiques unless expressly stated otherwise. Each boutique is responsible for
              the accuracy of its listings, fulfillment, and applicable return policies.
            </p>
          </Section>

          <Section id="accounts" number="3" title="Accounts">
            <p>
              You are responsible for maintaining the confidentiality of your account
              credentials and for all activity under your account. You agree to provide
              accurate information and notify us promptly of unauthorized use.
            </p>
          </Section>

          <Section id="orders" number="4" title="Orders & Payments">
            <p>
              When you place an order, you authorize Fash and our payment processor to charge
              your selected payment method. Prices, availability, and delivery estimates are
              set by boutiques and may change before checkout is complete. Refunds and returns
              are handled according to the applicable boutique policy and our dispute process.
            </p>
          </Section>

          <Section id="conduct" number="5" title="Acceptable Use">
            <p>
              You may not misuse the Service, interfere with other users, attempt unauthorized
              access, scrape content, or use the platform for unlawful purposes. We may suspend
              or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section id="disclaimers" number="6" title="Disclaimers">
            <p>
              The Service is provided on an &quot;as is&quot; and &quot;as available&quot; basis to the fullest
              extent permitted by law. Fash disclaims warranties of merchantability, fitness
              for a particular purpose, and non-infringement except where prohibited.
            </p>
          </Section>

          <Section id="liability" number="7" title="Limitation of Liability">
            <p>
              To the maximum extent permitted by law, Fash and its affiliates will not be
              liable for indirect, incidental, special, consequential, or punitive damages
              arising from your use of the Service.
            </p>
          </Section>

          <Section id="changes" number="8" title="Changes">
            <p>
              We may update these Terms from time to time. Material changes will be posted on
              this page with an updated &quot;Last Updated&quot; date. Continued use after changes
              become effective constitutes acceptance.
            </p>
          </Section>

          <Section id="contact" number="9" title="Contact">
            <p>
              Questions about these Terms? Email{' '}
              <a href="mailto:support@fashapp.com" className="text-primary hover:underline">
                support@fashapp.com
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
